import {useState, useEffect, useCallback} from 'react'
import {Checkbox, CheckboxField} from '@/components/tailwind/checkbox'
import {Label} from '@/components/tailwind/fieldset'
import {getServiceGroups, ServiceGroupItem} from '@/services/statistics-services'
import {useAuth} from '@/context/use-auth'
import log from '@/lib/logger'

interface ServiceGroupSelectorProps {
    onChange: (selectedIds: string[] | null) => void;
}

export function ServiceGroupSelector({onChange}: ServiceGroupSelectorProps) {
    const {token} = useAuth()
    const [groups, setGroups] = useState<ServiceGroupItem[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [selectAll, setSelectAll] = useState(true)

    useEffect(() => {
        async function fetchGroups() {
            if (!token) return
            try {
                const data = await getServiceGroups(token)
                setGroups(data)
            } catch (err) {
                log.error('Error fetching service groups:', err)
            } finally {
                setLoading(false)
            }
        }
        fetchGroups()
    }, [token])

    const handleSelectAllChange = useCallback((checked: boolean) => {
        setSelectAll(checked)
        if (checked) {
            setSelectedIds(new Set())
            onChange(null)
        } else {
            const allIds = new Set(groups.map(g => g.id))
            setSelectedIds(allIds)
            onChange(Array.from(allIds))
        }
    }, [groups, onChange])

    const handleGroupChange = useCallback((id: string, checked: boolean) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev)
            if (checked) {
                newSet.add(id)
            } else {
                newSet.delete(id)
            }

            if (newSet.size === 0 || newSet.size === groups.length) {
                setSelectAll(true)
                onChange(null)
                return new Set()
            } else {
                setSelectAll(false)
                onChange(Array.from(newSet))
                return newSet
            }
        })
    }, [groups.length, onChange])

    if (loading) {
        return (
            <div className="text-sm text-zinc-500 dark:text-zinc-400">
                Lade Servicegruppen...
            </div>
        )
    }

    if (groups.length === 0) {
        return (
            <div className="text-sm text-zinc-500 dark:text-zinc-400">
                Keine Servicegruppen vorhanden.
            </div>
        )
    }

    return (
        <div className="space-y-2">
            <CheckboxField>
                <Checkbox
                    name="select-all"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                />
                <Label className="font-medium">Alle Servicegruppen</Label>
            </CheckboxField>

            {!selectAll && (
                <div className="ml-6 space-y-1.5 border-l-2 border-zinc-200 dark:border-zinc-700 pl-4">
                    {groups.map(group => (
                        <CheckboxField key={group.id}>
                            <Checkbox
                                name={`group-${group.id}`}
                                checked={selectedIds.has(group.id)}
                                onChange={(checked) => handleGroupChange(group.id, checked)}
                            />
                            <Label>{group.name}</Label>
                        </CheckboxField>
                    ))}
                </div>
            )}
        </div>
    )
}
